// @ts-strict-ignore
import type {
  Brand,
  CatalogParams,
  CategoryFilter,
  ConditionFilter,
  IngredientFilterV2,
  OptimizedTaxonomyFilter,
  RelevanceFilter,
  RelevanceFilterCategories,
  RelevanceFilterTypes,
  SearchEntrypoint,
  SupplementTypeFilter,
  TaxonomyFilter,
} from "@shared/components/CatalogSearch/CatalogSearch";
import { SearchEntrypoints } from "@shared/components/CatalogSearch/CatalogSearch";
import type { RecentSearchesItem } from "@shared/data/queries/RecentSearchesQuery/RecentSearches.query";
import type { ParamsOptions } from "@shared/hooks/useUrlParams/useUrlParams";
import type { Typeahead as TypeaheadType } from "@shared/types/graphqlGenerated";

const useSharedCatalogSearchProvider = (
  catalogParams: CatalogParams,
  setCatalogParams: (params: CatalogParams, options?: ParamsOptions) => void
) => {
  const activeBrands: Brand[] = catalogParams.brands ?? [];

  const activeIngredients: IngredientFilterV2[] = catalogParams.ingredients ?? [];

  const activeRelevanceFilters: RelevanceFilter[] = catalogParams.relevanceFilters ?? [];

  const activeSupplementTypes: SupplementTypeFilter[] = catalogParams.supplementTypes ?? [];

  const activeConditions: ConditionFilter[] = catalogParams.conditions ?? [];

  const searchQuery: string = catalogParams.query ?? "";

  const activeCategories: CategoryFilter[] = catalogParams.categories ?? [];

  const activeTaxonomies: TaxonomyFilter[] = catalogParams.taxonomies ?? [];

  const activeOptimizedTaxonomies: OptimizedTaxonomyFilter[] =
    catalogParams.optimizedTaxonomies ?? [];

  const isFsTested: boolean = catalogParams.isFsTested ?? false;

  const setActiveBrands = (
    brands: Brand[],
    clearExistingFilters = false,
    searchEntryPoint?: SearchEntrypoint
  ) => {
    const newRelevanceFilters = getNewRelevanceFilters(brands, "Brand");

    let existingParams = { ...catalogParams };

    if (clearExistingFilters) {
      existingParams = {};
    }

    setCatalogParams({
      ...existingParams,
      brands: brands.map(brand => ({
        id: brand.id,
        name: brand.name,
        logo: "",
      })),
      relevanceFilters: newRelevanceFilters,
      ...(searchEntryPoint && { searchSessionEntrypoint: searchEntryPoint }),
    });
  };

  const setActiveTaxonomies = (taxonomyFilters: TaxonomyFilter[]) => {
    setCatalogParams({
      ...catalogParams,
      taxonomies: taxonomyFilters,
    });
  };

  const setActiveIngredients = (ingredientFilters: IngredientFilterV2[]) => {
    const newRelevanceFilters = getNewRelevanceFilters(ingredientFilters, "Ingredient");
    setCatalogParams({
      ...catalogParams,
      ingredients: ingredientFilters,
      relevanceFilters: newRelevanceFilters,
    });
  };

  const setActiveSupplementTypes = (supplementTypeFilters: SupplementTypeFilter[]) => {
    const newRelevanceFilters = getNewRelevanceFilters(supplementTypeFilters, "SupplementType");
    setCatalogParams({
      ...catalogParams,
      supplementTypes: supplementTypeFilters,
      relevanceFilters: newRelevanceFilters,
    });
  };

  const setActiveConditions = (conditionFilters: ConditionFilter[]) => {
    const newRelevanceFilters = getNewRelevanceFilters(conditionFilters, "Condition");
    setCatalogParams({
      ...catalogParams,
      conditions: conditionFilters,
      relevanceFilters: newRelevanceFilters,
    });
  };

  const setActiveOptimizedTaxonomies = (
    optimizedTaxonomies: OptimizedTaxonomyFilter[],
    clearAllPriorSearchParams = false
  ) => {
    if (optimizedTaxonomies?.length === 0) {
      const { optimizedTaxonomies: unusedOptimizedTaxonomies, ...otherParams } = catalogParams;
      setCatalogParams({ ...otherParams });

      return;
    }

    const isNewSearch = optimizedTaxonomies.every(filter => filter.type === "search");
    if (isNewSearch) {
      const optimizedTaxonomyFilters: OptimizedTaxonomyFilter[] = optimizedTaxonomies.map(
        taxonomy => {
          return {
            id: taxonomy.id,
            name: taxonomy.name,
            type: "filter",
          };
        }
      );

      setCatalogParams({
        optimizedTaxonomies: [...optimizedTaxonomies, ...optimizedTaxonomyFilters],
      });

      return;
    }

    setCatalogParams({
      ...(!clearAllPriorSearchParams && { ...catalogParams }),
      optimizedTaxonomies: [...optimizedTaxonomies],
    });
  };

  const setTypeaheadAsFilter = (
    typeaheadSearch: Pick<TypeaheadType, "id" | "name" | "entityType">,
    initFilters = {},
    clearAllPriorSearchParam: boolean = false
  ) => {
    switch (typeaheadSearch?.entityType) {
      case "SupplementType":
        {
          const supplementTypeExists = activeSupplementTypes.some(
            supplementType => supplementType.id === typeaheadSearch?.id
          );

          if (!supplementTypeExists) {
            setCatalogParams({
              ...(!clearAllPriorSearchParam && { ...catalogParams, ...initFilters }),
              ...(!clearAllPriorSearchParam && { query: searchQuery }),
              supplementTypes: [
                {
                  id: typeaheadSearch?.id,
                  name: typeaheadSearch?.name,
                },
              ],
              relevanceFilters: [
                ...activeRelevanceFilters,
                {
                  id: typeaheadSearch?.id,
                  category: "SupplementType",
                  query: typeaheadSearch?.name,
                },
              ],
            });
          }
        }

        break;
      case "Condition":
        {
          const conditionExists = activeConditions.some(
            condition => condition.id === typeaheadSearch?.id
          );
          const priorRelevance = clearAllPriorSearchParam ? [] : activeRelevanceFilters;

          if (!conditionExists) {
            setCatalogParams({
              ...(!clearAllPriorSearchParam && { ...catalogParams, ...initFilters }),
              ...(!clearAllPriorSearchParam && { query: searchQuery }),
              conditions: [
                {
                  id: typeaheadSearch?.id,
                  name: typeaheadSearch?.name,
                },
              ],
              relevanceFilters: [
                ...priorRelevance,
                {
                  id: typeaheadSearch?.id,
                  category: "Condition",
                  query: typeaheadSearch?.name,
                },
              ],
            });
          }
        }

        break;
      case "Brand": {
        const brandExists = activeBrands.some(brand => brand.id === typeaheadSearch?.id);
        const priorSelection = clearAllPriorSearchParam ? [] : activeBrands;
        const priorRelevance = clearAllPriorSearchParam ? [] : activeRelevanceFilters;

        if (!brandExists) {
          setCatalogParams({
            ...(!clearAllPriorSearchParam && { ...catalogParams, ...initFilters }),
            ...(!clearAllPriorSearchParam && { query: searchQuery }),
            brands: [
              ...priorSelection,
              {
                id: typeaheadSearch?.id,
                name: typeaheadSearch?.name,
                logo: "",
              },
            ],
            relevanceFilters: [
              ...priorRelevance,
              {
                id: typeaheadSearch?.id,
                category: "Brand",
                query: typeaheadSearch?.name,
              },
            ],
            searchSessionEntrypoint: SearchEntrypoints.TYPEAHEAD_BRANDS,
          });
        }

        break;
      }
      case "Ingredient": {
        const ingredientExists = activeIngredients.some(
          ingredient => ingredient.searchAttributes.id === typeaheadSearch?.id
        );
        const priorSelection = clearAllPriorSearchParam ? [] : activeIngredients;
        const priorRelevance = clearAllPriorSearchParam ? [] : activeRelevanceFilters;

        if (!ingredientExists) {
          setCatalogParams({
            ...(!clearAllPriorSearchParam && { ...catalogParams, ...initFilters }),
            ...(!clearAllPriorSearchParam && { query: searchQuery }),
            ingredients: [
              ...priorSelection,
              {
                name: typeaheadSearch?.name,
                searchAttributes: {
                  id: typeaheadSearch?.id,
                  addon: {
                    minDose: null,
                    maxDose: null,
                    unit: null,
                  },
                },
              },
            ],
            relevanceFilters: [
              ...priorRelevance,
              {
                id: typeaheadSearch?.id,
                category: "Ingredient",
                query: typeaheadSearch?.name,
              },
            ],
          });
        }
        break;
      }
      case "TaxonomySupplementType":
      case "TaxonomyCondition": {
        setCatalogParams({
          ...(!clearAllPriorSearchParam && { ...catalogParams, ...initFilters }),
          ...(!clearAllPriorSearchParam && { query: searchQuery }),
          optimizedTaxonomies: [
            {
              id: typeaheadSearch?.id,
              name: typeaheadSearch?.name,
              type: "search",
            },
            {
              id: typeaheadSearch?.id,
              name: typeaheadSearch?.name,
              type: "filter",
            },
          ],
        });

        break;
      }
    }
  };

  const setClickedRecentSearchTypeaheadToParam = (recentSearch: RecentSearchesItem) => {
    switch (recentSearch.filterableType) {
      case "SupplementType":
        setActiveSupplementTypes([
          { id: recentSearch.filterableId, name: recentSearch.filterableName },
        ]);

        setCatalogParams({
          supplementTypes: [
            {
              id: recentSearch.filterableId,
              name: recentSearch.filterableName,
            },
          ],
          relevanceFilters: [
            {
              id: recentSearch.filterableId,
              category: "SupplementType",
              query: recentSearch.filterableName,
            },
          ],
        });

        break;

      case "Condition":
        setCatalogParams({
          conditions: [
            {
              id: recentSearch.filterableId,
              name: recentSearch.filterableName,
            },
          ],
          relevanceFilters: [
            {
              id: recentSearch.filterableId,
              category: "Condition",
              query: recentSearch.filterableName,
            },
          ],
        });

        break;

      case "Brand":
        setCatalogParams({
          brands: [
            {
              id: recentSearch.filterableId,
              name: recentSearch.filterableName,
              logo: "",
            },
          ],
          relevanceFilters: [
            {
              id: recentSearch.filterableId,
              category: "Brand",
              query: recentSearch.filterableName,
            },
          ],
        });

        break;

      case "Ingredient":
        setCatalogParams({
          ingredients: [
            {
              name: recentSearch.filterableName,
              searchAttributes: {
                id: recentSearch.filterableId,
                addon: {
                  minDose: null,
                  maxDose: null,
                  unit: null,
                },
              },
            },
          ],
          relevanceFilters: [
            {
              id: recentSearch.filterableId,
              category: "Ingredient",
              query: recentSearch.filterableName,
            },
          ],
        });

        break;

      default:
        setCatalogParams({
          query: recentSearch.query,
        });
    }
  };

  const getNewRelevanceFilters = (
    newFilters: RelevanceFilterTypes,
    type: RelevanceFilterCategories
  ) => {
    const newIds = newFilters.map(filter =>
      type === "Ingredient" ? filter.searchAttributes.id : filter.id
    );
    const newRelevanceFilters = activeRelevanceFilters.filter(
      relevanceFilter => newIds.includes(relevanceFilter.id) || relevanceFilter.category !== type
    );

    newIds.forEach(id => {
      if (!activeRelevanceFilters.map(filter => filter.id).includes(id)) {
        newRelevanceFilters.push({ id, category: type, query: "" });
      }
    });

    return newRelevanceFilters;
  };

  const setIsFsTested = (isTested: boolean) => {
    setCatalogParams({
      ...catalogParams,
      isFsTested: isTested,
    });
  };

  const searchSessionEntrypoint: SearchEntrypoint = catalogParams.searchSessionEntrypoint;

  const setSearchSessionEntrypoint = (searchEntrypoint: SearchEntrypoint) => {
    setCatalogParams({
      ...catalogParams,
      searchSessionEntrypoint: searchEntrypoint,
    });
  };

  return {
    activeBrands,
    activeIngredients,
    activeRelevanceFilters,
    activeSupplementTypes,
    activeConditions,
    activeCategories,
    activeTaxonomies,
    activeOptimizedTaxonomies,
    searchSessionEntrypoint,
    searchQuery,
    setActiveBrands,
    setActiveIngredients,
    setActiveSupplementTypes,
    setActiveConditions,
    setActiveTaxonomies,
    setActiveOptimizedTaxonomies,
    setTypeaheadAsFilter,
    setClickedRecentSearchTypeaheadToParam,
    isFsTested,
    setIsFsTested,
    setSearchSessionEntrypoint,
  };
};

export { useSharedCatalogSearchProvider };
